// src/components/staffing/TeamAssemblyWorkspace.jsx
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Logger } from '../../utils/logger';
import { COLOR_GOOD, COLOR_WARN, BRAND_PURPLE } from '../../utils/colors';
import { apiFetch, apiGet } from '../../utils/api';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Chip,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Tune as TuneIcon,
  EventAvailable as AvailableIcon,
  Psychology as SkillsIcon,
  TrendingUp as MarginIcon,
  BookmarkAddOutlined as BookmarkDraftIcon,
} from '@mui/icons-material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import RoleAccordionSection from './RoleAccordionSection';
import staffingService from '../../services/staffingService';
import { CURRENT_DATE } from '../../utils/constants';
import { searchProjects } from '../../services/projectService';
import CandidateDetailsModal from './CandidateDetailsModal';
import TeamOptimizationDashboard from './TeamOptimizationDashboard';
import TeamConfigurationManager from './TeamConfigurationManager';
import { useTeamAnalysis } from './hooks/useTeamAnalysis';
import { useSaveManager } from './hooks/useSaveManager';
import { useCandidateSearch } from './hooks/useCandidateSearch';
import { useDragDrop } from './hooks/useDragDrop';
import RoleDetailsModal from './RoleDetailsModal';
import OpportunityNameLink from '../common/OpportunityNameLink';

const logger = new Logger('TeamAssemblyWorkspace');

// Date formatting utility
const formatDate = (dateString) => {
  if (!dateString || dateString === 'Unknown' || dateString === 'TBD') {
    return 'TBD';
  }
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'TBD';
    }
    
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${month}/${day}/${year}`;
  } catch (error) {
    return 'TBD';
  }
};

/**
 * TeamAssemblyWorkspace - The main interface for building and optimizing teams
 * Now uses persistent state from useStaffingState hook
 */
const TeamAssemblyWorkspace = ({
  staffingId,
  opportunityData,
  staffingStateManager,
  onBack,
  onSave,
  onComplete
}) => {
  // Use persistent state from the state manager
  const {
    staffingState,
    setActiveTab,
    setSelectedRole,
    setCandidatePool,
    setIsSearchingCandidates,
    setClaudeCommentary,
    setCandidatePerformanceData,
    setOptimizationResult,
    setTeamConfigurations,
    setActiveConfigId,
    setSelectedRoleForDetails,
    setSelectedCandidateForDetails,
    getCandidatePerformanceData,
    setRoleCandidates,
    setRoleSearching,
    setRoleSearchCandidate,
    updateStaffingState,
    setGlobalStrategy,
    setRoleStrategy,
  } = staffingStateManager;

  // Extract persistent state values
  const activeTab = staffingState.activeTab;
  const selectedRole = staffingState.selectedRole;
  const candidatePool = staffingState.candidatePool;
  const isSearchingCandidates = staffingState.isSearchingCandidates;
  const claudeCommentary = staffingState.claudeCommentary;
  const roleCandidatePools = staffingState.roleCandidatePools || {};
  const globalStrategy = staffingState.globalStrategy || 'balanced';
  const roleStrategies = staffingState.roleStrategies || {};
  const optimizationResult = staffingState.optimizationResult;
  const teamConfigurations = staffingState.teamConfigurations;
  const activeConfigId = staffingState.activeConfigId;
  const selectedRoleForDetails = staffingState.selectedRoleForDetails;
  const selectedCandidateForDetails = staffingState.selectedCandidateForDetails;
  const candidatePerformanceData = staffingState.candidatePerformanceData;

  // Core state (still local as these are specific to current staffing session)
  const [staffingData, setStaffingData] = useState(null);

  // Chemistry data injected from a saved config so the hook skips re-fetching on load.
  // Cleared whenever the team composition changes so fresh fetches work normally.
  const [injectedChemistryData, setInjectedChemistryData] = useState(null);

  // Lifted from Dashboard so analysis data persists across tab switches
  const { data: hookTeamAnalysisData, loading: teamAnalysisLoading } = useTeamAnalysis(
    staffingData?.id || staffingData?.opportunity_id,
    staffingData?.roles,
    injectedChemistryData  // seeds hook + suppresses fetch when loaded from config
  );

  // Merged chemistry data: prefer injected (restored from config), fall back to hook's live fetch
  const teamAnalysisData = injectedChemistryData || hookTeamAnalysisData;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Save / configuration state ───────────────────────────────────────────
  const {
    handleSave,
    handleSaveDraft,
    setEnrichSavePending,
    setLoadedConfigurationId,
    setLoadedConfigurationName,
    isSaving,
    saveStatus,
    saveError, setSaveError,
    lastSaveTime,
    loadedConfigurationId,
    loadedConfigurationName,
  } = useSaveManager({
    staffingData,
    optimizationResult,
    hookTeamAnalysisData,
    teamAnalysisData,
    onSave,
  });

  // UI state that shouldn't persist (modal open states stay local)
  const [isRoleDetailsOpen, setIsRoleDetailsOpen] = useState(false);
  const [isCandidateDetailsOpen, setIsCandidateDetailsOpen] = useState(false);

  // ── Candidate search / selection / organisation ──────────────────────────
  const getEffectiveStrategy = useCallback((role) => {
    const roleId = role?.id;
    return (roleId && roleStrategies[roleId]) || globalStrategy || 'balanced';
  }, [roleStrategies, globalStrategy]);

  const {
    allEmployees,
    allTechFitOpen, setAllTechFitOpen,
    loadingPerformanceData,
    handleFindCandidates,
    handleSelectSearchCandidate,
    handleAnalyzeChemistry,
    fetchCandidatePerformanceData,
  } = useCandidateSearch({
    staffingData,
    roleCandidatePools,
    setRoleCandidates,
    setRoleSearching,
    setRoleSearchCandidate,
    candidatePerformanceData,
    setCandidatePerformanceData,
    getEffectiveStrategy,
  });

  // ── Accordion expansion state ─────────────────────────────────────────────
  const [expandedRoles, setExpandedRoles] = useState(() => new Set());

  const handleRoleToggle = (roleId) => {
    setExpandedRoles(prev => {
      const next = new Set(prev);
      next.has(roleId) ? next.delete(roleId) : next.add(roleId);
      return next;
    });
  };

  // When staffingData first loads, expand all open roles
  useEffect(() => {
    if (!staffingData?.roles) return;
    setExpandedRoles(prev => {
      const next = new Set(prev);
      staffingData.roles.forEach(r => { if (r.status === 'open') next.add(r.id); });
      return next;
    });
  }, [staffingData?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- only on initial load

  // Auto-collapse roles as they become assigned
  useEffect(() => {
    if (!staffingData?.roles) return;
    setExpandedRoles(prev => {
      const next = new Set(prev);
      staffingData.roles.forEach(r => { if (r.status === 'assigned') next.delete(r.id); });
      return next;
    });
  }, [staffingData?.roles]);

  // ── Role-assignment logic ─────────────────────────────────────────────────
  const {
    assignCandidateToRole,
    unassignCandidateFromRole,
    removeAssignment,
  } = useDragDrop({
    staffingData,
    setStaffingData,
    optimizationResult,
    setOptimizationResult,
    setLoadedConfigurationId,
    setLoadedConfigurationName,
    setInjectedChemistryData,
    onUnassigned: (roleId) => setExpandedRoles(prev => new Set([...prev, roleId])),
  });

  // Log state restoration for debugging
  useEffect(() => {
    logger.debug('🔄 TeamAssemblyWorkspace: Using persistent state:', {
      activeTab,
      hasSelectedRole: !!selectedRole,
      candidatePoolSize: candidatePool.length,
      hasClaudeCommentary: !!claudeCommentary,
      hasOptimizationResult: !!optimizationResult
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // handleSelectSearchCandidate — moved to useCandidateSearch hook

  // fetchCandidatePerformanceData — moved to useCandidateSearch hook

  const handleClearSearchCandidate = (role) => {
    const roleId = role?.id || role?.role_name;
    if (roleId) setRoleSearchCandidate(roleId, null);
  };

  // Load real staffing data
  useEffect(() => {
    const loadStaffingData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // ✅ CLEAR CANDIDATE POOL when loading new opportunity
        logger.debug('🧹 Clearing candidate pool for new opportunity:', staffingId);
        setCandidatePool([]);
        setSelectedRole(null);
        setClaudeCommentary('');
        updateStaffingState({ roleCandidatePools: {} });
        setOptimizationResult(null);
        
        logger.debug('Loading staffing data for ID:', staffingId);
        
        // Try to get existing workflow state first
        let workflowData = null;
        try {
          workflowData = await staffingService.getWorkflowState(staffingId);
          logger.debug('Existing workflow data:', workflowData);
        } catch (error) {
          logger.debug('No existing workflow found, will create new one');
        }
        
        // Get pipeline/opportunity data based on staffing ID
        let projectData = null;
        try {
          // Get all pipeline opportunities and find a match
          const pipelineData = await apiGet('pipeline');
          const opportunities = pipelineData.opportunities || [];

          // Find opportunity that matches the staffing ID
          projectData = opportunities.find(opp =>
            opp.opportunity_id === staffingId ||
            opp.project_id === staffingId ||
            opp.id === staffingId
          );

          if (projectData) {
            logger.debug('Found pipeline opportunity data:', projectData);
          } else {
            logger.debug('No matching opportunity found for ID:', staffingId);
          }
        } catch (error) {
          logger.error('Error loading pipeline data:', error);
        }
        
        // Extract client name from staffing ID if it contains query information
        let extractedClientName = 'Unknown Client';
        let extractedProjectName = 'Team Assembly Project';
        
        // Try to extract client name from staffing ID (which might be a query)
        if (staffingId && typeof staffingId === 'string') {
          // Look for client patterns in the staffing ID
          const clientPatterns = [
            /at ([A-Z][a-zA-Z\s]+(?:Healthcare|Health|Medical|Hospital|Clinic))/i,
            /project at ([A-Z][a-zA-Z\s]+)/i,
            /for ([A-Z][a-zA-Z\s]+(?:Healthcare|Health|Medical|Hospital|Clinic))/i,
            /client ([A-Z][a-zA-Z\s]+)/i,
            /(Blue River Financial)/i,
            /(Regional Medical Center)/i,
            /(Metro Healthcare)/i,
            /(Cardinal Health)/i,
            /(Healthcare Systems Pro)/i,
            /(Premier Healthcare)/i,
            /(Gateway Healthcare)/i,
          ];
          
          for (const pattern of clientPatterns) {
            const match = staffingId.match(pattern);
            if (match && match[1]) {
              extractedClientName = match[1].trim();
              logger.debug('🏢 CLIENT EXTRACTED from staffing ID:', extractedClientName);
              break;
            }
          }
          
          // Extract project name patterns
          const projectPatterns = [
            /(Medical Records System Upgrade)/i,
            /(Enterprise Architecture Modernization)/i,
            /(API Modernization)/i,
            /(Digital Transformation)/i,
            /(Cloud Migration)/i,
            /(System Integration)/i,
          ];
          
          for (const pattern of projectPatterns) {
            const match = staffingId.match(pattern);
            if (match && match[1]) {
              extractedProjectName = match[1].trim();
              logger.debug('📋 PROJECT EXTRACTED from staffing ID:', extractedProjectName);
              break;
            }
          }
        }

        // Create staffing data structure - prioritize opportunityData if available
        let staffingDataObj = {
          id: staffingId,
          projectName: opportunityData?.projectName || extractedProjectName,
          clientName: opportunityData?.clientName || extractedClientName,
          projectType: opportunityData?.projectType || 'project',
          deadline: opportunityData?.deadline ? new Date(opportunityData.deadline) : new Date(Date.now() + 90 * 24 * 60 * 60 * 1000),
          priority: opportunityData?.priority || 'medium',
          stage: opportunityData?.originalData?.stage || 'Unknown',
          winProbability: opportunityData?.originalData?.win_probability_percentage || 'Unknown',
          reviScore: opportunityData?.originalData?.revi_score || 'N/A',
          estimatedCloseDate: opportunityData?.originalData?.estimated_close_date || 'Unknown',
          start_date: opportunityData?.originalData?.estimated_start_date || opportunityData?.originalData?.start_date || null,
          pursuitTeam: opportunityData?.pursuitTeam || [],
          salesOwner: opportunityData?.salesOwner || {},
          roles: [],
          progress: {
            totalRoles: 0,
            filledRoles: 0,
            completionPercentage: 0
          },
          teamPerformance: {
            overallScore: 7.5,
            skillsCoverage: 70,
            teamChemistry: 8.0,
            riskLevel: 'medium',
            recommendations: [
              'Team assembly in progress',
              'Add candidates to roles to see detailed insights',
              'Performance metrics will update as team is built'
            ]
          }
        };
        
        // Use project data if available
        if (projectData) {
          staffingDataObj.projectName = projectData.opportunity_name || projectData.project_name || projectData.name || staffingDataObj.projectName;
          staffingDataObj.clientName = projectData.client_name || projectData.clientName || staffingDataObj.clientName;
          staffingDataObj.projectType = projectData.opportunity_id ? 'opportunity' : 'project';
          
          // ✅ CRITICAL FIX: Store the actual opportunity_id from database for backend queries
          staffingDataObj.opportunity_id = projectData.opportunity_id;
          logger.debug('🎯 EXTRACTED OPPORTUNITY_ID from projectData:', projectData.opportunity_id);
          
          // Use project's estimated closing date as deadline
          if (projectData.estimated_close_date) {
            staffingDataObj.deadline = new Date(projectData.estimated_close_date);
          } else if (projectData.expected_close_date) {
            staffingDataObj.deadline = new Date(projectData.expected_close_date);
          }

          // Extract start date from project data
          if (projectData.estimated_start_date) {
            staffingDataObj.start_date = projectData.estimated_start_date;
          } else if (projectData.start_date) {
            staffingDataObj.start_date = projectData.start_date;
          }
          
          // FIXED: Use project roles if available - check both locations
          let rolesArray = null;
          
          // First check if roles are directly in the project data (your data structure)
          if (projectData.roles && Array.isArray(projectData.roles)) {
            rolesArray = projectData.roles;
            logger.debug('🎯 FOUND ROLES in projectData.roles:', rolesArray.length, 'roles');
          }
          // Fallback to nested structure if needed
          else if (projectData.project_data && projectData.project_data.roles && Array.isArray(projectData.project_data.roles)) {
            rolesArray = projectData.project_data.roles;
            logger.debug('🎯 FOUND ROLES in projectData.project_data.roles:', rolesArray.length, 'roles');
          }
          
          if (rolesArray) {
            staffingDataObj.roles = rolesArray.map((role, index) => ({
              // ✅ FIXED: Use stable role_id from backend (UUID), fallback to generating index-based for backward compatibility
              id: role.role_id || `role-${index + 1}`,
              role_name: role.role_name,
              level: role.level,
              skills: Array.isArray(role.skills) ? role.skills : [],
              required_experience_years: role.required_experience_years,
              total_hours: role.estimated_hours,
              hourly_rate: role.billing_rate,
              billing_rate: role.billing_rate,  // ✅ FIXED: Preserve billing_rate for margin analysis
              role_start_date: role.role_start_date || null,
              role_end_date: role.role_end_date || null,
              status: 'open',
              assigned_employee_id: null,
              assigned_employee_name: null,
              fit_score: null
            }));
            
            logger.debug('✅ MAPPED ROLES for UI:', staffingDataObj.roles.map(r => ({ id: r.id, role_name: r.role_name, level: r.level })));
          } else {
            logger.debug('❌ NO ROLES FOUND in projectData - checking opportunityData next');
          }
        }
        
        // ADDITIONAL FIX: Also check opportunityData directly for roles
        if ((!staffingDataObj.roles || staffingDataObj.roles.length === 0) && opportunityData && opportunityData.originalData && opportunityData.originalData.roles && Array.isArray(opportunityData.originalData.roles)) {
          logger.debug('🎯 FOUND ROLES in opportunityData.originalData.roles:', opportunityData.originalData.roles.length, 'roles');
          
          staffingDataObj.roles = opportunityData.originalData.roles.map((role, index) => ({
            // ✅ FIXED: Use stable role_id from backend (UUID), fallback to generating index-based for backward compatibility
            id: role.role_id || `role-${index + 1}`,
            role_name: role.role_name,
            level: role.level,
            skills: Array.isArray(role.skills) ? role.skills : [],
            required_experience_years: role.required_experience_years,
            total_hours: role.estimated_hours,
            hourly_rate: role.billing_rate,
            billing_rate: role.billing_rate,  // ✅ FIXED: Preserve billing_rate for margin analysis
            role_start_date: role.role_start_date || null,
            role_end_date: role.role_end_date || null,
            status: 'open',
            assigned_employee_id: null,
            assigned_employee_name: null,
            fit_score: null
          }));
          
          logger.debug('✅ MAPPED ROLES from opportunityData:', staffingDataObj.roles.map(r => ({ id: r.id, role_name: r.role_name, level: r.level })));
        }
        
        // ENTERPRISE: No default roles - if no roles defined, show empty state
        // staffingDataObj.roles will remain empty if no project roles exist
        
        // Apply existing workflow assignments if available
        logger.debug('🔍 CHECKING workflow data for assignments:', workflowData);
        
        // Check multiple possible locations for assignments
        let assignments = [];
        if (workflowData && workflowData.status === 'success' && workflowData.workflow) {
          const workflow = workflowData.workflow;
          logger.debug('🔍 WORKFLOW object:', workflow);
          
          if (workflow.assignments) {
            assignments = Array.isArray(workflow.assignments) ? workflow.assignments : [];
          }
        } else if (workflowData && workflowData.assignments) {
          assignments = Array.isArray(workflowData.assignments) ? workflowData.assignments : [];
        }
        
        logger.debug('🔄 LOADING ASSIGNMENTS from database:', assignments);
        logger.debug('🔄 ROLES to match against:', staffingDataObj.roles.map(r => ({ id: r.id, role_name: r.role_name })));
        
        if (assignments.length > 0) {
          const usedAssignmentIndices = new Set();
          staffingDataObj.roles = staffingDataObj.roles.map(role => {
            // Each assignment can only be used once — prevents duplicate-name collision
            let idx = assignments.findIndex((a, i) =>
              !usedAssignmentIndices.has(i) && a.role_id && role.id && a.role_id === role.id);
            if (idx === -1)
              idx = assignments.findIndex((a, i) =>
                !usedAssignmentIndices.has(i) && a.role_name === role.role_name);
            if (idx === -1)
              idx = assignments.findIndex((a, i) =>
                !usedAssignmentIndices.has(i) && a.role_name && role.role_name &&
                a.role_name.toLowerCase().includes(role.role_name.toLowerCase()));

            if (idx !== -1) {
              usedAssignmentIndices.add(idx);
              const assignment = assignments[idx];
              logger.debug(`✅ MATCHED assignment for role ${role.role_name}:`, assignment);
              return {
                ...role,
                status: 'assigned',
                assigned_employee_id: assignment.employee_id,
                assigned_employee_name: assignment.employee_name,
                fit_score: assignment.fit_score || 8.5
              };
            }
            logger.debug(`❌ NO MATCH found for role ${role.role_name} (id: ${role.id})`);
            return role;
          });
        }

        // Check for a saved lineup matching the current team composition
        try {
          const configResponse = await apiFetch(`team-optimization/configurations/project/${staffingId}`);

          if (configResponse.ok) {
            const configData = await configResponse.json();
            const configurations = configData.configurations || [];

            // Compute fingerprint from the team as loaded via workflow
            const currentFingerprint = staffingDataObj.roles
              .filter(r => r.status === 'assigned' && r.assigned_employee_id)
              .map(r => `${r.id ?? ''}:${r.assigned_employee_id ?? ''}`)
              .sort()
              .join('|');

            const matchingConfig = currentFingerprint
              ? configurations.find(c => c.team_fingerprint === currentFingerprint)
              : null;

            if (matchingConfig) {
              logger.debug('✅ Found saved lineup matching current team:', matchingConfig.configuration_name);
              setLoadedConfigurationId(matchingConfig.configuration_id);
              setLoadedConfigurationName(matchingConfig.configuration_name);

              if (matchingConfig.optimization_analysis) {
                const full = matchingConfig.optimization_analysis.full_components || {};
                setOptimizationResult({
                  overall_score: matchingConfig.optimization_analysis.overall_score,
                  components: full,
                  claude_analysis: matchingConfig.claude_analysis || null,
                  detailed_analysis: matchingConfig.detailed_analysis || null,
                  success_probability: matchingConfig.optimization_analysis.predicted_success_rate || null,
                  from_cache: false,
                });
              }
              if (matchingConfig.team_analysis_data) {
                setInjectedChemistryData(matchingConfig.team_analysis_data);
              }
            } else {
              logger.debug('ℹ️ No saved lineup matches current team composition');
            }
          }
        } catch (error) {
          logger.debug('ℹ️ Could not check for saved lineups:', error.message);
        }

        // Update progress based on assignments
        const filledRoles = staffingDataObj.roles.filter(r => r.status === 'assigned').length;
        const totalRoles = staffingDataObj.roles.length;
        const completionPercentage = totalRoles > 0 ? Math.round((filledRoles / totalRoles) * 100) : 0;
        
        logger.debug('Progress calculation:', { filledRoles, totalRoles, completionPercentage });
        
        staffingDataObj.progress = {
          totalRoles: totalRoles,
          filledRoles: filledRoles,
          completionPercentage: completionPercentage
        };
        
        // Initialize workflow on backend if it doesn't exist
        if (!workflowData) {
          try {
            await staffingService.initializeWorkflow(
              staffingId,
              staffingDataObj.projectType,
              staffingDataObj.roles
            );
            logger.debug('Initialized new workflow for:', staffingId);
          } catch (error) {
            logger.error('Error initializing workflow:', error);
            // Continue anyway - frontend can work without backend workflow
          }
        }
        
        setStaffingData(staffingDataObj);
        setIsLoading(false);
      } catch (err) {
        logger.error('Error loading staffing data:', err);
        setError('Failed to load staffing data: ' + (err.message || 'Unknown error'));
        setIsLoading(false);
      }
    };

    if (staffingId) {
      loadStaffingData();
    }
  }, [staffingId]);

  // getExistingTeamMembers — moved to useCandidateSearch hook

  // handleFindCandidates — moved to useCandidateSearch hook

  // useEffect for handleFindCandidatesRef — moved to useCandidateSearch hook

  // handleDragEnd, assignCandidateToRole, unassignCandidateFromRole, removeAssignment — moved to useDragDrop hook

  // Handle role details modal
  const handleOpenRoleDetails = (role) => {
    setSelectedRoleForDetails(role);
    setIsRoleDetailsOpen(true);
  };

  const handleCloseRoleDetails = () => {
    setIsRoleDetailsOpen(false);
    setSelectedRoleForDetails(null);
  };

  // Handle candidate details modal
  const handleViewCandidateDetails = (candidate) => {
    setSelectedCandidateForDetails(candidate);
    setIsCandidateDetailsOpen(true);
  };

  const handleCloseCandidateDetails = () => {
    setIsCandidateDetailsOpen(false);
    setSelectedCandidateForDetails(null);
  };

  // handleAnalyzeChemistry — moved to useCandidateSearch hook

  // fetchAssignedEmployees — moved to useCandidateSearch hook

  // organizeCandidates — moved to useCandidateSearch hook

  // Save progress
  // Save logic is managed by useSaveManager (auto-saves on analysis completion).

  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading Team Assembly Workspace...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!staffingData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">No staffing data available</Alert>
      </Box>
    );
  }

  return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Paper sx={{ p: 2, borderRadius: 0, borderBottom: '1px solid rgba(255, 255, 255, 0.12)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={onBack} sx={{ mr: 2 }}>
                <BackIcon />
              </IconButton>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  <OpportunityNameLink opportunity={{ opportunity_id: staffingData.opportunity_id, name: staffingData.projectName, client_name: staffingData.clientName }}>
                    {staffingData.projectName}
                  </OpportunityNameLink>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {staffingData.clientName} • Team Assembly
                  {staffingData.opportunity_id && (
                    <> • Opportunity #{staffingData.opportunity_id}</>
                  )}
                </Typography>
                {staffingData.stage !== 'Unknown' && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Stage: {staffingData.stage} | Win Probability: {staffingData.winProbability} | POV©: {staffingData.reviScore || 'N/A'} | Close: {formatDate(staffingData.estimatedCloseDate)} | Start: {formatDate(staffingData.start_date || staffingData.originalData?.estimated_start_date || staffingData.originalData?.start_date)}
                  </Typography>
                )}
              </Box>
            </Box>
            
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Save validation error — shown inline, never blanks the page */}
              {saveError && (
                <Chip
                  label={saveError}
                  color="error"
                  size="small"
                />
              )}
              {/* Save Status Indicator */}
              {saveStatus && (
                <Chip
                  label={
                    saveStatus === 'saving' ? 'Saving...' :
                    saveStatus === 'saved' ? 'Saved' :
                    'Save Error'
                  }
                  color={
                    saveStatus === 'saved' ? 'success' :
                    saveStatus === 'error' ? 'error' :
                    'default'
                  }
                  size="small"
                  sx={{
                    opacity: saveStatus === 'saved' ? 0.7 : 1,
                    transition: 'opacity 0.3s'
                  }}
                />
              )}

              <Chip
                label={`${staffingData.progress.filledRoles}/${staffingData.progress.totalRoles} roles`}
                color={staffingData.progress.completionPercentage === 100 ? 'success' : 'warning'}
                variant="outlined"
              />
            </Box>
          </Box>
          
          {/* ── Staffing Strategy Hero (Team Assembly tab only) ────────────────── */}
          {activeTab === 0 && <Box sx={{
            mt: 2,
            py: 1.5,
            px: 3,
            borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(123,31,162,0.18) 0%, rgba(74,20,140,0.12) 100%)',
            border: '1px solid rgba(123,31,162,0.4)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.2,
          }}>
            <Typography sx={{
              fontWeight: 700,
              fontSize: '0.7rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#ce93d8',
            }}>
              Staffing Strategy
            </Typography>

            <ToggleButtonGroup
              value={globalStrategy}
              exclusive
              onChange={(_, val) => val && setGlobalStrategy(val)}
              sx={{
                '& .MuiToggleButton-root': {
                  fontSize: '0.85rem',
                  px: 2.5,
                  py: 0.9,
                  textTransform: 'none',
                  borderColor: 'rgba(123,31,162,0.4)',
                  color: 'text.secondary',
                  display: 'flex',
                  gap: 0.75,
                  fontWeight: 500,
                },
                '& .Mui-selected': {
                  backgroundColor: '#7b1fa2 !important',
                  color: 'white !important',
                  fontWeight: 700,
                  boxShadow: '0 2px 8px rgba(123,31,162,0.5)',
                },
                '& .Mui-selected:hover': {
                  backgroundColor: '#6a1b9a !important',
                },
                '& .MuiToggleButton-root:hover:not(.Mui-selected)': {
                  backgroundColor: 'rgba(123,31,162,0.1)',
                  color: '#ce93d8',
                },
              }}
            >
              {[
                { value: 'balanced',         Icon: TuneIcon,      label: 'Balanced',         tip: 'Equal weight on skills, availability, team chemistry, and cost. Best for most projects — surfaces well-rounded candidates who score well across all dimensions.' },
                { value: 'available_now',    Icon: AvailableIcon, label: 'Available Now',    tip: 'Heavy emphasis on immediate availability. Finds people who can start on day one — but solid skills are still required. Best when the start date is non-negotiable.' },
                { value: 'skills_first',     Icon: SkillsIcon,    label: 'Skills First',     tip: 'Prioritizes the strongest technical match. Minor scheduling gaps are acceptable for the right person. Other factors still count. Best for complex or specialized engagements.' },
                { value: 'margin_enhancing', Icon: MarginIcon,    label: 'Margin Enhancing', tip: 'Surfaces cost-efficient candidates without sacrificing quality. When skills are comparable, lower-cost people rank higher and Claude flags the cost difference.' },
              ].map(({ value, Icon, label, tip }) => (
                <Tooltip key={value} arrow placement="bottom" title={
                  <Box sx={{ p: 0.5, maxWidth: 260 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>{label}</Typography>
                    <Typography variant="caption">{tip}</Typography>
                  </Box>
                }>
                  <ToggleButton value={value}>
                    <Icon sx={{ fontSize: '1rem' }} />{label}
                  </ToggleButton>
                </Tooltip>
              ))}
            </ToggleButtonGroup>
          </Box>}

          {/* Progress Bar */}
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant="determinate"
              value={staffingData.progress.completionPercentage}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  bgcolor: staffingData.progress.completionPercentage === 100 ? COLOR_GOOD : COLOR_WARN
                }
              }}
            />
          </Box>
        </Paper>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.12)', flexShrink: 0 }}
          >
            <Tab label="Team Assembly" />
            <Tab label="Team Analysis" />
            <Tab label="Team Lineups" />
          </Tabs>

          <Box sx={{ flexGrow: 1, p: 2, overflow: 'auto' }}>
            {activeTab === 0 && (
              staffingData.roles.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    This opportunity has no staffing roles specified in the database
                  </Typography>
                </Box>
              ) : (
                <>
                  {staffingData.roles.map((role) => (
                    <RoleAccordionSection
                      key={role.id}
                      role={role}
                      rolePool={roleCandidatePools[role.id] || { candidates: [], commentary: '', isSearching: false }}
                      isExpanded={expandedRoles.has(role.id)}
                      onToggle={handleRoleToggle}
                      staffingData={staffingData}
                      allTechFitOpen={allTechFitOpen}
                      setAllTechFitOpen={setAllTechFitOpen}
                      allEmployees={allEmployees}
                      onFindCandidates={handleFindCandidates}
                      onViewDetails={handleViewCandidateDetails}
                      onAnalyzeChemistry={handleAnalyzeChemistry}
                      onSelectSearchCandidate={handleSelectSearchCandidate}
                      onClearSearchCandidate={handleClearSearchCandidate}
                      onOpenRoleDetails={handleOpenRoleDetails}
                      assignCandidateToRole={assignCandidateToRole}
                      unassignCandidateFromRole={unassignCandidateFromRole}
                      removeAssignment={removeAssignment}
                      effectiveStrategy={roleStrategies[role.id] || globalStrategy}
                      roleStrategy={roleStrategies[role.id] || null}
                      globalStrategy={globalStrategy}
                      onSetRoleStrategy={(sid) => setRoleStrategy(role.id, sid)}
                    />
                  ))}
                  {staffingData.progress.completionPercentage === 100 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 1 }}>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => setActiveTab(1)}
                        sx={{ fontWeight: 'bold', px: 4, backgroundColor: BRAND_PURPLE, color: '#fff' }}
                      >
                        Analyze This Team
                      </Button>
                    </Box>
                  ) : staffingData.progress.completionPercentage > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 1 }}>
                      <Button
                        variant="outlined"
                        size="large"
                        startIcon={<BookmarkDraftIcon />}
                        onClick={handleSaveDraft}
                        disabled={isSaving}
                        sx={{
                          fontWeight: 'bold', px: 4,
                          borderColor: COLOR_WARN, color: COLOR_WARN,
                          '&:hover': { borderColor: COLOR_WARN, bgcolor: 'rgba(255,167,38,0.08)' },
                        }}
                      >
                        Save This Draft
                      </Button>
                    </Box>
                  )}
                </>
              )
            )}
              
              {activeTab === 1 && (
                <TeamOptimizationDashboard
                  staffingData={staffingData}
                  optimizationResult={optimizationResult}
                  loadedConfigurationId={loadedConfigurationId}
                  loadedConfigurationName={loadedConfigurationName}
                  teamAnalysisData={teamAnalysisData}
                  teamAnalysisLoading={teamAnalysisLoading}
                  onOptimizationUpdate={(result) => {
                    const prevResult = optimizationResult;
                    setOptimizationResult(result);

                    // If new enriched data arrived (detailed analysis or client intro), re-save current slot
                    const hasNewDetailedAnalysis = result?.detailed_analysis && result.detailed_analysis !== prevResult?.detailed_analysis;
                    const hasNewClientIntro = result?.client_introduction && result.client_introduction !== prevResult?.client_introduction;
                    if (hasNewDetailedAnalysis || hasNewClientIntro) {
                      setEnrichSavePending(true);
                    }
                  }}
                />
              )}
              
              {activeTab === 2 && (
                <TeamConfigurationManager
                  staffingData={staffingData}
                  optimizationResult={optimizationResult}
                  initialActiveSlot={0}
                  onConfigurationLoad={async (config) => {
                    logger.debug('🔄 Loading configuration:', config.configuration_name);
                    
                    // 1. Restore team assignments from saved configuration
                    const usedConfigIndices = new Set();
                    const updatedRoles = staffingData.roles.map(role => {
                      const members = config.team_members;
                      let memberIdx = members.findIndex((m, i) =>
                        !usedConfigIndices.has(i) && m.role_id && role.id && m.role_id === role.id);
                      if (memberIdx === -1)
                        memberIdx = members.findIndex((m, i) =>
                          !usedConfigIndices.has(i) && (m.role === role.role_name || m.role_name === role.role_name));
                      if (memberIdx !== -1) usedConfigIndices.add(memberIdx);
                      const savedMember = memberIdx !== -1 ? members[memberIdx] : null;

                      if (savedMember) {
                        const matchedBy = savedMember.role_id && role.id ? 'role_id' : 'role_name';
                        logger.debug(`✅ Restoring assignment (matched by ${matchedBy}): ${savedMember.employee_name} → ${role.role_name} (${role.id})`);

                        // Use hourly_rate (the rate set at assignment time) — do not fall back to role_billing_rate
                        const displayRate = savedMember.hourly_rate || null;

                        // Debug what rates we have
                        logger.debug(`💰 Restoring rates for ${savedMember.employee_name}:`, {
                          hourly_rate: savedMember.hourly_rate,
                          role_billing_rate: savedMember.role_billing_rate,
                          employee_cost_rate: savedMember.employee_cost_rate,
                          displayRate: displayRate,
                          fit_score: savedMember.fit_score
                        });

                        return {
                          ...role,
                          status: 'assigned',
                          assigned_employee_id: savedMember.employee_id,
                          assigned_employee_name: savedMember.employee_name,
                          fit_score: savedMember.fit_score,
                          // ✅ FIX: Use saved assigned_tech_score if available, fallback to fit_score
                          assigned_tech_score: savedMember.assigned_tech_score || savedMember.fit_score || null,
                          assigned_hourly_rate: displayRate ? `$${displayRate}/hr` : null
                        };
                      }
                      
                      // Clear assignment if not in saved configuration
                      return {
                        ...role,
                        status: 'open',
                        assigned_employee_id: null,
                        assigned_employee_name: null,
                        fit_score: null,
                        assigned_tech_score: null,
                        assigned_hourly_rate: null
                      };
                    });
                    
                    // Calculate updated progress
                    const filledRoles = updatedRoles.filter(r => r.status === 'assigned').length;
                    const totalRoles = updatedRoles.length;
                    const completionPercentage = totalRoles > 0 ? Math.round((filledRoles / totalRoles) * 100) : 0;
                    
                    // 2. Update staffing data with restored assignments
                    setStaffingData(prev => ({
                      ...prev,
                      roles: updatedRoles,
                      progress: {
                        totalRoles,
                        filledRoles,
                        completionPercentage
                      }
                    }));
                    
                    // 3. ✅ FIXED: Instantly restore optimization result from saved data (no API calls needed)
                    logger.debug('🔧 Instantly restoring optimization result from saved config:', config.configuration_name);

                    // ✅ NEW: Check if we have full_components saved (complete detailed data)
                    const hasFullComponents = config.optimization_analysis?.full_components;

                    logger.debug('🔥🔥🔥 LOADING CONFIG:', config.configuration_name);
                    logger.debug('🔥🔥🔥 HAS FULL COMPONENTS?', !!hasFullComponents);
                    logger.debug('🔥🔥🔥 HAS CLAUDE ANALYSIS?', !!config.claude_analysis);
                    logger.debug('🔥🔥🔥 HAS DETAILED ANALYSIS?', !!config.detailed_analysis);

                    logger.debug('📦 Loading configuration - checking for full_components:', {
                      hasOptimizationAnalysis: !!config.optimization_analysis,
                      hasFullComponents: !!hasFullComponents,
                      hasClaudeAnalysis: !!config.claude_analysis,
                      hasDetailedAnalysis: !!config.detailed_analysis,
                      optimizationAnalysisKeys: config.optimization_analysis ? Object.keys(config.optimization_analysis) : [],
                      fullComponentsKeys: hasFullComponents ? Object.keys(hasFullComponents) : []
                    });

                    // Create complete optimization result structure from saved data
                    const restoredOptimizationResult = {
                      overall_score: config.optimization_analysis.overall_score,
                      success_probability: config.optimization_analysis.predicted_success_rate,
                      confidence_interval: config.optimization_analysis.confidence_interval || [0.0, 1.0],
                      fromSavedConfig: true, // Flag to indicate this analysis is from a loaded config, not fresh
                      loadedConfigId: config.configuration_id, // Track which config this came from
                      components: hasFullComponents ? config.optimization_analysis.full_components : {
                        technical_coverage: {
                          score: config.optimization_analysis.technical_coverage,
                          coverage_percentage: config.comparison_metrics.skill_coverage_percentage,
                          // Add placeholder structures that TeamOptimizationDashboard expects
                          individual_role_fits: config.team_members.map(member => ({
                            employee_name: member.employee_name,
                            assigned_role: member.role,
                            role_fit_score: member.fit_score || 8.0,
                            skills_breakdown: [] // Placeholder - saved configs don't need detailed breakdown
                          })),
                          project_gaps: [],
                          project_strengths: [],
                          role_competency_gaps: [],
                          role_competency_strengths: [],
                          project_skills_total: staffingData?.roles?.flatMap(r => r.skills || []).length || 0
                        },
                        collaboration_score: {
                          score: config.optimization_analysis.collaboration_score
                        },
                        disc_compatibility: {
                          score: config.optimization_analysis.disc_compatibility
                        },
                        strengths_synergy: {
                          score: config.optimization_analysis.strengths_synergy
                        },
                        experience_balance: {
                          score: config.optimization_analysis.experience_balance
                        },
                        risk_assessment: {
                          risk_level: config.optimization_analysis.risk_assessment,
                          identified_risks: config.comparison_metrics.risk_factors
                        },
                        margin_analysis: {
                          total_cost: config.comparison_metrics.estimated_cost || null,
                          total_revenue: config.comparison_metrics.estimated_revenue || config.estimated_revenue || null,
                          total_profit: (config.comparison_metrics.estimated_revenue || config.estimated_revenue || 0) - (config.comparison_metrics.estimated_cost || 0),
                          overall_margin: config.comparison_metrics.margin_percentage || config.margin_percentage || null,
                          individual_margins: config.team_members.map(member => {
                            const billingRate = member.role_billing_rate || member.hourly_rate || null;
                            const costRate = member.employee_cost_rate || null;
                            const allocatedHours = member.allocated_hours || null;

                            // Only calculate if we have all required data
                            const revenue = (billingRate && allocatedHours) ? billingRate * allocatedHours : null;
                            const cost = (costRate && allocatedHours) ? costRate * allocatedHours : null;
                            const profit = (billingRate && costRate && allocatedHours) ? (billingRate - costRate) * allocatedHours : null;
                            const marginPercentage = (billingRate && costRate) ? ((billingRate - costRate) / billingRate * 100) : null;

                            return {
                              employee_name: member.employee_name,
                              role: member.role,
                              billing_rate: billingRate,
                              cost_rate: costRate,
                              allocated_hours: allocatedHours,
                              revenue: revenue,
                              cost: cost,
                              profit: profit,
                              margin_percentage: marginPercentage
                            };
                          })
                        },
                        performance_prediction: {
                          combined_dir: config.optimization_analysis.overall_score - 5,
                          average_dir: config.optimization_analysis.overall_score - 5,
                          average_csat: 4.0, // Default CSAT
                          individual_dir_scores: config.team_members.map(() => config.optimization_analysis.overall_score - 5)
                        },
                        // Enhanced team chemistry components
                        communication_skills: { score: config.optimization_analysis.collaboration_score * 0.95 },
                        presentation_skills: { score: config.optimization_analysis.collaboration_score * 0.85 },
                        teamwork_skills: { score: config.optimization_analysis.collaboration_score * 1.05 },
                        flexibility_skills: { score: config.optimization_analysis.collaboration_score * 0.9 },
                        collaboration_history: { 
                          score: config.optimization_analysis.collaboration_score * 0.8,
                          data_available: false
                        },
                        industry_client_experience: {
                          score: 7.5,
                          experience_level: 'Strong domain expertise and client industry experience'
                        }
                      },
                      // ✅ CRITICAL: Preserve the complete Claude analysis (both summary and details)
                      claude_analysis: config.claude_analysis || '',
                      detailed_analysis: config.detailed_analysis || config.claude_analysis || '', // Use detailed if available, fallback to summary
                      client_introduction: config.client_introduction || null
                    };
                    
                    logger.debug('🔥🔥🔥 ABOUT TO SET OPTIMIZATION RESULT');
                    logger.debug('🔥🔥🔥 RESTORED RESULT:', restoredOptimizationResult);
                    logger.debug('🔥🔥🔥 HAS DETAILED_ANALYSIS?', !!restoredOptimizationResult.detailed_analysis);
                    logger.debug('🔥🔥🔥 DETAILED_ANALYSIS LENGTH:', (restoredOptimizationResult.detailed_analysis || '').length);
                    logger.debug('🔥🔥🔥 HAS CLAUDE_ANALYSIS?', !!restoredOptimizationResult.claude_analysis);
                    logger.debug('🔥🔥🔥 CLAUDE_ANALYSIS LENGTH:', (restoredOptimizationResult.claude_analysis || '').length);
                    logger.debug('🔥🔥🔥 HAS COMPONENTS?', !!restoredOptimizationResult.components);
                    logger.debug('🔥🔥🔥 hasFullComponents from config?', !!hasFullComponents);
                    logger.debug('🔥🔥🔥 config.detailed_analysis?', !!config.detailed_analysis, 'length:', (config.detailed_analysis || '').length);
                    logger.debug('🔥🔥🔥 config.claude_analysis?', !!config.claude_analysis, 'length:', (config.claude_analysis || '').length);

                    logger.debug('✅ Instantly restored optimization result with saved Claude analysis:', {
                      claudeAnalysisLength: (config.claude_analysis || '').length,
                      overallScore: restoredOptimizationResult.overall_score,
                      teamMembers: config.team_members.length
                    });

                    // 4. Update optimization result state immediately
                    setOptimizationResult(restoredOptimizationResult);

                    // 4b. Set loaded configuration state
                    setLoadedConfigurationId(config.configuration_id);
                    setLoadedConfigurationName(config.configuration_name);

                    // 6. Inject saved chemistry data so the hook skips re-fetching
                    if (config.team_analysis_data) {
                      setInjectedChemistryData(config.team_analysis_data);
                    }

                    // 7. Switch to optimization tab to show restored results
                    setActiveTab(1);

                    // NOTE: no auto-save on load — the config is already in MongoDB and
                    // we no longer need to stamp updated_at on every load.

                    logger.debug('✅ Configuration loaded successfully:', config.configuration_name);
                  }}
                />
              )}
          </Box>
        </Box>

        {/* Candidate Details Modal */}
        <CandidateDetailsModal
          open={isCandidateDetailsOpen}
          onClose={handleCloseCandidateDetails}
          candidate={selectedCandidateForDetails}
        />

        {/* Role Details Modal */}
        <RoleDetailsModal
          open={isRoleDetailsOpen}
          onClose={handleCloseRoleDetails}
          role={selectedRoleForDetails}
        />

      </Box>
  );
};

TeamAssemblyWorkspace.propTypes = {
  staffingId: PropTypes.string.isRequired,
  opportunityData: PropTypes.object,
  staffingStateManager: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  onComplete: PropTypes.func
};

export default TeamAssemblyWorkspace;
